import React, { ReactElement, useEffect, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { Input, MenuProps, SelectProps, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { Select } from 'common/components/antd/Select/Select';
import { Table } from 'common/components/antd/Table/Table';
import { ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { IS_ADMIN_FE } from 'common/constants/general-access-role.constants';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from 'common/constants/table.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { isValidGUID } from 'common/utils/form.utils';
import { useGetBranchesMultiRoleQuery_MultiRole } from 'domains/branches/queries/branch.query';
import {
  TCounterpartyAvailableBranchesResData,
  TCounterpartyBranchesResData,
} from 'domains/branches/types/branch.types';
import { useGetCounterpartiesQuery } from 'domains/counterparties/queries/counterparty.query';
import { TCounterpartiesListReqParams } from 'domains/counterparties/types/counterparty.types';
import { useGetCounterpartyCustomersListQuery_MultiRole } from 'domains/customers/queries/customers.query';
import { TCustomersListReqParams } from 'domains/customers/types/customers.types';
import { TScopeResData } from 'domains/roles/types/roles.types';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import { SDangerActionItem } from './customers.page.style';
import {
  ECounterpartyAction,
  ECustomersColumnKey,
  EModalType,
  EUserManagementCustomersPageGetParams,
  EUserManagementCustomersPageGetParamsActionTypes,
} from './enums/customer.enum';
import UserManagementCreateCustomersModal from './modals/create-edit-customers.modal';
import { getColumns } from './table/customers-table.columns';
import {
  TTablePaginationConfig,
  TUserRoleManagementCustomersLoaderData,
  TCustomersTableRecord,
  TCustomersModalData,
} from './types/customers.types';

const UserManagementCustomersPage = (): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const loaderData = useLoaderData() as TUserRoleManagementCustomersLoaderData;
  const [selectedCounterparty, setSelectedCounterparty] = useState(ENV_COUNTERPARTY_GUID);
  const [modalData, setModalData] = useState<TCustomersModalData | {}>({});
  const [searchStr, setSearchStr] = useState<string>('');
  const [paginationData, setPaginationData] = useState<TTablePaginationConfig>({
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const [selectedBranch, setSelectedBranch] =
    useState<string>((loaderData.branchesData as TCounterpartyAvailableBranchesResData).branches?.[0].guid);

  const {
    data: counterpartiesData,
    isError: isCounterpartiesDataError,
    error: counterpartiesDataError,
  } = useGetCounterpartiesQuery(
    {},
    {
      refetchOnMount: false,
      enabled: IS_ADMIN_FE,
    },
  );

  const {
    data: branchesData,
    isError: isBranchesDataError,
    error: branchesDataError,
  } = useGetBranchesMultiRoleQuery_MultiRole(selectedCounterparty, {}, loaderData.hasAccessForAllBranches, {
    refetchOnMount: false,
    select: (data: any) => {
      const redoData = {
        ...data,
        ...(loaderData.hasAccessForAllBranches ? { branches: data.list } : null),
      };

      return redoData;
    },
  });

  const addParams = (str: string): { wallet_user_guids: string[] } | { search_string: string } | null => {
    if (str) {
      if (isValidGUID(str)) {
        return { wallet_user_guids: [str] };
      }
      return { search_string: str };
    }

    return null;
  }

  const {
    data: customersData,
    isLoading: isCustomersDataLoading,
    isError: isCustomersDataError,
    error: customersDataError,
  } = useGetCounterpartyCustomersListQuery_MultiRole(
    selectedCounterparty,
    selectedBranch,
    {
      limit: paginationData.pageSize,
      offset: (paginationData.page - 1) * paginationData.pageSize,
      ...addParams(searchStr),
    },
    loaderData.hasAccessForAllBranches,
    {
      refetchOnMount: false,
      placeholderData: keepPreviousData,
    },
  );

  const isError = isBranchesDataError || isCustomersDataError || isCounterpartiesDataError;
  const error = branchesDataError || customersDataError || counterpartiesDataError;

  useEffect(() => {
    if (isError) {
      console.error(error);
    }
  }, [isError, error]);

  useEffect(() => {
    const searchParams = new URLSearchParams(document.location.search);
    const pageAction = searchParams.get(EUserManagementCustomersPageGetParams.Action);

    if (pageAction === EUserManagementCustomersPageGetParamsActionTypes.Create) {
      setModalData({
        type: EModalType.Create,
        rowData: {},
        branchesData,
        counterpartiesData,
      });
    }
  }, [document.location.search, counterpartiesData, branchesData]);

  const actions: MenuProps['items'] = [
    {
      key: ECounterpartyAction.Edit,
      label: t('usrRoleMng.customers.table.menu.action.edit'),
    },
    {
      key: ECounterpartyAction.Delete,
      label: <SDangerActionItem>{t('usrRoleMng.customers.table.menu.action.delete')}</SDangerActionItem>,
    },
  ];

  const onItemActionClick = ({ record, actionKey }: { record: TCustomersTableRecord; actionKey: string }): void => {
    switch (actionKey) {
      case ECounterpartyAction.Edit: {
        setModalData({
          type: EModalType.Edit,
          rowData: record,
          branchesData,
          counterpartiesData,
        });
        break;
      }
      case ECounterpartyAction.Delete: {
        throw new Error('Deletion is not implemented');
        break;
      }
      default: {
        break;
      }
    }
  };

  const columns = getColumns({
    menuItems: actions,
    onItemActionClick,
  });

  const handlePaginationChange = (pageParam: number, pageSizeParam: number): void => {
    setPaginationData({
      page: pageParam,
      pageSize: pageSizeParam,
    });
  };

  const customersTableData: TCustomersTableRecord[] | undefined = customersData?.customers?.map(
    (item): TCustomersTableRecord => ({
      ...item,
      user: `${item?.name} ${item?.surname}`,
      status: '',
      external_id: item?.login,
      customer_guid: item?.wallet_user_guid,
    }),
  );

  const onChangeBranchHandler = (branch: string): void => {
    setSelectedBranch(branch);
  }

  const onCancelModalHandler = (): void => {
    setModalData({});
    navigate(ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_CUSTOMERS);
  };

  const counterpartyOptions = (counterpartiesData?.counterparties || []).map((c) => ({
    label: c.name,
    value: c.guid,
  }));

  const selectedCounterpartyOption = counterpartyOptions.find((opt) => opt.value === selectedCounterparty);

  const changeCounterparty: SelectProps['onChange'] = (value) => {
    setSelectedBranch('');
    setSelectedCounterparty(value);
  }

  const handleSearch = (searchValue: string): void => {
    setSearchStr(searchValue);
  }

  return (
    <>
      <Space style={{ padding: '16px' }}>
        {IS_ADMIN_FE && (
          <Select
            style={{
              width: 272,
            }}
            defaultValue={selectedCounterparty}
            value={selectedCounterpartyOption}
            options={counterpartyOptions}
            onChange={changeCounterparty}
          />
        )}
        <Select
          style={{ width: '280px' }}
          placeholder={t('usrRoleMng.customers.filterButtonBranch.placeholder') as string}
          onChange={onChangeBranchHandler}
          value={selectedBranch || []}
          allowClear
        >
          {((branchesData as TCounterpartyAvailableBranchesResData)?.branches || []).map((option) => (
            <Select.Option key={option.guid} value={option.guid}>
              {option?.name}
            </Select.Option>
          ))}
        </Select>

        <Input.Search
          style={{ width: '280px' }}
          placeholder={t('usrRoleMng.customers.searchButton.placeholder') as string}
          onSearch={handleSearch}
          allowClear
        />
      </Space>
      <Table
        pagination={{
          style: {
            marginRight: 30,
          },
          showQuickJumper: true,
          showSizeChanger: true,
          total: customersData?.total || 0,
          pageSize: paginationData.pageSize,
          current: paginationData.page,
          onChange: handlePaginationChange,
        }}
        rowKey={ECustomersColumnKey.WalletUserGuid}
        dataSource={customersTableData}
        columns={columns}
        loading={isCustomersDataLoading}
      />
      <UserManagementCreateCustomersModal
        isOpen={!!Object.keys(modalData).length}
        onCancelModalHandler={onCancelModalHandler}
        data={modalData as TCustomersModalData}
      />
    </>
  );
};

export const loader = async (): Promise<{
  customersData: TCustomersListReqParams | {},
  counterpartiesData: TCounterpartiesListReqParams | {},
  branchesData: TCounterpartyBranchesResData | TCounterpartyAvailableBranchesResData | {},
  hasAccessForAllBranches: boolean,
}> => {
  try {
    let counterpartiesData: TCounterpartiesListReqParams = {};

    if (IS_ADMIN_FE) {
      const counterpartiesDataQuery = queryKeysClient[EQueryKeys.CounterpartyQueryKeys].getCounterpartiesList();
      counterpartiesData =
        queryClient.getQueryData(counterpartiesDataQuery.queryKey) ??
        (await queryClient.fetchQuery(counterpartiesDataQuery));
    }

    const queryScopeListData =
      queryKeysClient[EQueryKeys.RbacQueryKeys].getCounterpartyScopesList(ENV_COUNTERPARTY_GUID);
    const scopeListData: TScopeResData =
      queryClient.getQueryData(queryScopeListData.queryKey) ??
      (await queryClient.fetchQuery(queryScopeListData));

    const hasAccessForAllBranches =
      ((scopeListData as TScopeResData).scopes.find(({ scope }) => scope === 'employees')?.is_allowed &&
        (scopeListData as TScopeResData).scopes.find(({ scope }) => scope === 'branches')?.is_allowed) ||
      false;

    const getBranchesMultiRoleQuery = queryKeysClient[EQueryKeys.BranchQueryKeys].getBranches_MultiRole(
      ENV_COUNTERPARTY_GUID,
      {},
      hasAccessForAllBranches,
    );
    const branchesData: TCounterpartyBranchesResData | TCounterpartyAvailableBranchesResData =
      queryClient.getQueryData(getBranchesMultiRoleQuery.queryKey) ??
      (await queryClient.fetchQuery(getBranchesMultiRoleQuery));

    const defaultBranchGUID = hasAccessForAllBranches
      ? ''
      : (branchesData as TCounterpartyAvailableBranchesResData).branches[0].guid;

    const getCustomersMultiRoleQuery = queryKeysClient[EQueryKeys.CustomerQueryKeys].getCustomersList_MultiRole(
      ENV_COUNTERPARTY_GUID,
      defaultBranchGUID,
      {
        offset: (DEFAULT_PAGE - 1) * DEFAULT_PAGE_SIZE,
        limit: DEFAULT_PAGE_SIZE,
      },
      hasAccessForAllBranches,
    );

    const customersData: TCustomersListReqParams =
      queryClient.getQueryData(getCustomersMultiRoleQuery.queryKey) ??
      (await queryClient.fetchQuery(getCustomersMultiRoleQuery));

    return {
      hasAccessForAllBranches,
      branchesData,
      customersData,
      counterpartiesData,
    };
  } catch (error) {
    console.error(error);

    return {
      hasAccessForAllBranches: false,
      branchesData: {},
      customersData: {},
      counterpartiesData: {},
    };
  }
};

export default UserManagementCustomersPage;
